import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { DiaryManagementNavTabs } from "@/features/admin/components/DiaryManagementNavTabs";
import type { AdminEntryValueFilters } from "@/features/admin/entry-values/api/adminEntryValuesApi";
import { useAdminEntryValues } from "@/features/admin/entry-values/api/adminEntryValuesApi";
import {
    ADMIN_ENTRY_VALUES_COLUMNS,
    AdminEntryValuesTable,
} from "@/features/admin/entry-values/components/AdminEntryValuesTable";
import type { AdminEntryValuesFilterValues } from "@/features/admin/entry-values/components/AdminEntryValuesToolbar";
import {
    emptyAdminEntryValuesFilterValues,
    AdminEntryValuesToolbar,
} from "@/features/admin/entry-values/components/AdminEntryValuesToolbar";
import {
    ADMIN_TABLE_PAGE_SIZE,
    AdminColumnsDialog,
    AdminTablePagination,
    useDebouncedValue,
} from "@/features/admin/tables";
import { useNav } from "@/utils/nav";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";

const EntryValuesSearchZ = z.object({
    q: z.string().optional(),
    page: z.coerce.number().int().min(1).optional(),
    entryId: z.string().optional(),
    inputId: z.string().optional(),
    diaryId: z.string().optional(),
    formId: z.string().optional(),
    organisationId: z.string().optional(),
});

export const Route = createFileRoute("/_app/admin/entry-values")({
    component: AdminEntryValuesPage,
    validateSearch: (search) => EntryValuesSearchZ.parse(search),
});

function AdminEntryValuesPage() {
    useNav([
        { label: "Admin", route: { to: "/admin" } },
        { label: "Entry values", route: { to: "/admin/entry-values" } },
    ]);

    const search = Route.useSearch();
    const navigate = Route.useNavigate();

    const [searchText, setSearchText] = useState(search.q ?? "");
    const debouncedSearch = useDebouncedValue(searchText.trim(), 300);

    const [filterValues, setFilterValues] = useState<AdminEntryValuesFilterValues>(() => ({
        ...emptyAdminEntryValuesFilterValues,
        entryId: search.entryId ?? null,
        inputId: search.inputId ?? null,
        diaryId: search.diaryId ?? null,
        formId: search.formId ?? null,
        organisationId: search.organisationId ?? null,
    }));

    const [visibleKeys, setVisibleKeys] = useState(() =>
        ADMIN_ENTRY_VALUES_COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key)
    );

    const page = search.page ?? 1;

    useEffect(() => {
        const urlQ = search.q ?? "";
        if (debouncedSearch === urlQ) return;
        void navigate({
            search: (prev) => ({
                ...prev,
                q: debouncedSearch || undefined,
                page: 1,
            }),
            replace: true,
        });
    }, [debouncedSearch, navigate, search.q]);

    useEffect(() => {
        setFilterValues((prev) => ({
            ...prev,
            entryId: search.entryId ?? null,
            inputId: search.inputId ?? null,
            diaryId: search.diaryId ?? null,
            formId: search.formId ?? null,
            organisationId: search.organisationId ?? null,
        }));
    }, [search.entryId, search.inputId, search.diaryId, search.formId, search.organisationId]);

    const filters: AdminEntryValueFilters = useMemo(
        () => ({
            q: debouncedSearch || undefined,
            entryId: filterValues.entryId ?? undefined,
            inputId: filterValues.inputId ?? undefined,
            diaryId: filterValues.diaryId ?? undefined,
            formId: filterValues.formId ?? undefined,
            organisationId: filterValues.organisationId ?? undefined,
            page,
            pageSize: ADMIN_TABLE_PAGE_SIZE,
        }),
        [debouncedSearch, filterValues, page]
    );

    const { data, isLoading, isFetching, error } = useAdminEntryValues(filters);
    const entryValues = data?.entryValues ?? [];

    return (
        <AdminPageLayout
            title="Entry values"
            description="Browse individual answer values across entries. Filter by entry, diary, organisation, or form."
        >
            <div className="space-y-4">
                <DiaryManagementNavTabs />

                <AdminEntryValuesToolbar
                    searchText={searchText}
                    onSearchTextChange={setSearchText}
                    filterValues={filterValues}
                    onApplyFilters={(values) => {
                        setFilterValues(values);
                        void navigate({
                            search: (prev) => ({
                                ...prev,
                                page: 1,
                                entryId: values.entryId ?? undefined,
                                inputId: values.inputId ?? undefined,
                                diaryId: values.diaryId ?? undefined,
                                formId: values.formId ?? undefined,
                                organisationId: values.organisationId ?? undefined,
                            }),
                            replace: true,
                        });
                    }}
                    isApplying={isFetching}
                >
                    <AdminColumnsDialog
                        columns={ADMIN_ENTRY_VALUES_COLUMNS}
                        visibleKeys={visibleKeys}
                        onSave={setVisibleKeys}
                    />
                </AdminEntryValuesToolbar>

                {filterValues.entryId || filterValues.diaryId ? (
                    <p className="text-muted-foreground text-xs">
                        {filterValues.entryId ? (
                            <>
                                Filtered to entry <span className="font-mono">{filterValues.entryId}</span>
                            </>
                        ) : null}
                        {filterValues.diaryId ? (
                            <>
                                {filterValues.entryId ? " · " : null}
                                Diary <span className="font-mono">{filterValues.diaryId}</span>
                            </>
                        ) : null}
                        {" · "}
                        <button
                            type="button"
                            className="text-sky-700 underline"
                            onClick={() => {
                                const cleared = { ...filterValues, entryId: null, diaryId: null };
                                setFilterValues(cleared);
                                void navigate({
                                    search: (prev) => ({
                                        ...prev,
                                        entryId: undefined,
                                        diaryId: undefined,
                                        page: 1,
                                    }),
                                    replace: true,
                                });
                            }}
                        >
                            Clear ID filters
                        </button>
                    </p>
                ) : null}

                {error && (
                    <div className="bg-red-50 p-4 border border-red-200 rounded-md text-red-800 text-sm">
                        Error loading entry values: {error instanceof Error ? error.message : "Unknown error"}
                    </div>
                )}

                {isLoading ? (
                    <ui.Skeleton className="w-full h-64" />
                ) : (
                    !error && (
                        <div className={isFetching ? "opacity-70 transition-opacity" : undefined}>
                            <AdminEntryValuesTable entryValues={entryValues} visibleKeys={visibleKeys} />
                            <div className="mt-3">
                                <AdminTablePagination
                                    page={page}
                                    totalCount={data?.totalCount ?? 0}
                                    pageSize={ADMIN_TABLE_PAGE_SIZE}
                                    isFetching={isFetching}
                                    onPageChange={(nextPage) => {
                                        void navigate({
                                            search: (prev) => ({ ...prev, page: nextPage }),
                                            replace: true,
                                        });
                                    }}
                                />
                            </div>
                        </div>
                    )
                )}
            </div>
        </AdminPageLayout>
    );
}
